import { motion } from 'framer-motion'
import LightbulbIcon from '../components/icons/LightbulbIcon'
import GoalIcon from '../components/icons/GoalIcon'
import StarIcon from '../components/icons/StarIcon'
import ProcessIcon from '../components/icons/ProcessIcon'
import TechShowcase from '../components/TechShowcase'
import SEO from '../components/SEO'
import Logo from '../components/Logo'
import { generateOrganizationSchema } from '../lib/structured-data-utils'
import CtaSection from '../components/CtaSection'

const About = () => {
  const currentUrl = typeof window !== 'undefined' ? window.location.href : '';

  const values = [
    {
      title: 'Innovation First',
      description: 'We leverage AI and stay ahead of technology trends to deliver cutting-edge solutions.',
      icon: LightbulbIcon,
    },
    {
      title: 'Client Success',
      description: 'Your success is our success. We\'re invested in your long-term growth.',
      icon: GoalIcon,
    },
    {
      title: 'Quality Driven',
      description: 'Every line of code, every design decision is made with quality in mind.',
      icon: StarIcon,
    },
    {
      title: 'Transparent Process',
      description: 'Clear communication and honest timelines throughout every project.',
      icon: ProcessIcon,
    },
  ]

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  }

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6
      }
    }
  }

  const organizationSchema = generateOrganizationSchema();

  return (
    <div className="pt-20 bg-white dark:bg-gray-900">
      <SEO
        title="About Demirix - IT Solutions Provider in Dubai, UAE"
        description="Demirix is a leading Dubai-based IT solutions provider specializing in web development, cloud computing, AI integration, and blockchain solutions. Transforming businesses in UAE and globally with innovative technology."
        keywords={['about Demirix', 'IT company Dubai', 'technology solutions UAE', 'web development company Dubai', 'cloud computing Dubai', 'software development Dubai UAE', 'AI solutions provider Dubai', 'blockchain development Dubai', 'IT consulting Dubai', 'digital transformation Dubai']}
        url={currentUrl}
        image="https://demirix.com/about-hero.jpg"
        structuredData={organizationSchema}
      />
      {/* Hero Section */}
      <section className="section-padding bg-gradient-to-b from-gray-50 to-white dark:from-gray-800 dark:to-gray-900">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            {/* Big centered logo */}
            <div className="flex justify-center mb-8">
              <Logo className="h-24 md:h-32 lg:h-40" />
            </div>
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 dark:text-white mb-6">
              About Demirix
            </h1>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
              Serving clients worldwide. We're a team of passionate technologists 
              committed to transforming businesses through innovative IT and AI-powered solutions.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Story Section */}
      <section className="section-padding">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6 }}
            className="grid grid-cols-1 lg:grid-cols-2 gap-12"
          >
            <div className="text-center lg:text-left">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-6">
                Our Story
              </h2>
              <div className="space-y-4 text-gray-600 dark:text-gray-300">
                <p>
                  Demirix was born from a simple belief:
                  every business deserves access to world-class technology solutions, regardless of size or budget.
                </p>
                <p>
                  We started as a small team of developers who were frustrated by the one size fits all approach
                  of many IT companies. We knew there had to be a better way, a more personal, more tailored approach
                  that puts the client's unique needs first.
                </p>
                <p>
                  Today, we're proud to serve clients across the globe, from ambitious startups to established
                  enterprises, helping them leverage technology to achieve their business goals. Ready to start your project? <a href="/contact" className="text-primary-600 dark:text-primary-400 hover:underline font-semibold">Get in touch with us</a>.
                </p>
              </div>
            </div>
            
            <div className="bg-gray-50 dark:bg-gray-800 p-8 rounded-2xl border border-gray-100 dark:border-gray-700">
              <h3 className="text-2xl font-semibold text-gray-900 dark:text-white mb-6">Why We're Different</h3>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <svg className="w-6 h-6 text-primary-600 dark:text-primary-400 mr-3 mt-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  <span className="text-gray-600 dark:text-gray-300">We listen first, then build solutions that fit your exact needs</span>
                </li>
                <li className="flex items-start">
                  <svg className="w-6 h-6 text-primary-600 dark:text-primary-400 mr-3 mt-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  <span className="text-gray-600 dark:text-gray-300">Transparent pricing with no hidden fees or surprises</span>
                </li>
                <li className="flex items-start">
                  <svg className="w-6 h-6 text-primary-600 dark:text-primary-400 mr-3 mt-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  <span className="text-gray-600 dark:text-gray-300">Long-term partnerships, not just one-time transactions</span>
                </li>
                <li className="flex items-start">
                  <svg className="w-6 h-6 text-primary-600 dark:text-primary-400 mr-3 mt-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  <span className="text-gray-600 dark:text-gray-300">
                    <a href="/services?service=ai" className="text-primary-600 dark:text-primary-400 hover:underline font-semibold">AI-powered solutions</a> combined with human expertise
                  </span>
                </li>
                <li className="flex items-start">
                  <svg className="w-6 h-6 text-primary-600 dark:text-primary-400 mr-3 mt-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  <span className="text-gray-600 dark:text-gray-300">Serving clients globally with 24/7 support</span>
                </li>
              </ul>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Values Section */}
      <section className="section-padding bg-gray-50 dark:bg-gray-800">
        <div className="container-custom">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={containerVariants}
          >
            <motion.h2 
              variants={itemVariants}
              className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white text-center mb-12"
            >
              Our Values
            </motion.h2>
            <motion.div 
              variants={containerVariants}
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8"
            >
              {values.map((value, index) => (
                <motion.div
                  key={index}
                  variants={itemVariants}
                  className="text-center p-6 bg-white dark:bg-gray-700 rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300 border border-gray-100 dark:border-gray-600"
                >
                  <div className="mb-4 flex justify-center">
                    <value.icon className="w-16 h-16" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
                    {value.title}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300">
                    {value.description}
                  </p>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Technology Showcase */}
      <TechShowcase />


      {/* CTA Section */}
      <CtaSection
        heading="Let's Build Something Amazing Together"
        description="Ready to transform your business with technology? We're here to help you every step of the way."
        primary={{ kind: 'link', label: 'Start Your Project', href: '/contact', asAnchor: true }}
        secondary={{ kind: 'link', label: 'View Our Services', href: '/services', asAnchor: true }}
        size="lg"
      />
    </div>
  )
}

export default About